export interface ActionTooltipContent {
  title: string;
  titleSuffix?: string;
  description?: string | null;
  statLines?: ActionTooltipStatLine[];
}

export interface ActionTooltipTextSegment {
  text: string;
  color?: string;
}

export type ActionTooltipStatLine = string | ActionTooltipTextSegment[];

export function parseInputList(rawInputs: unknown): string[] {
  let parsedInputs: unknown = rawInputs;

  if (typeof rawInputs === 'string') {
    try {
      parsedInputs = JSON.parse(rawInputs) as unknown;
    } catch {
      parsedInputs = rawInputs;
    }
  }

  if (Array.isArray(parsedInputs)) {
    return parsedInputs.filter((entry): entry is string => typeof entry === 'string');
  }
  if (typeof parsedInputs === 'string' && parsedInputs.trim().length > 0) {
    return [parsedInputs.trim()];
  }
  return [];
}

function renderActionTooltipContent(tooltip: HTMLElement, content: ActionTooltipContent): void {
  tooltip.innerHTML = '';

  const titleEl = document.createElement('div');
  titleEl.className = 'skill-tooltip-title';
  titleEl.textContent = content.title;
  if (content.titleSuffix && content.titleSuffix.trim().length > 0) {
    const suffixEl = document.createElement('span');
    suffixEl.textContent = `   ${content.titleSuffix}`;
    suffixEl.style.fontSize = '0.5em';
    suffixEl.style.opacity = '0.9';
    titleEl.appendChild(suffixEl);
  }
  tooltip.appendChild(titleEl);

  if (content.description && content.description.trim().length > 0) {
    const descEl = document.createElement('div');
    descEl.className = 'skill-tooltip-desc';
    descEl.textContent = content.description;
    tooltip.appendChild(descEl);
  }

  if (content.statLines && content.statLines.length > 0) {
    const statsEl = document.createElement('div');
    statsEl.className = 'skill-tooltip-stats';
    for (const line of content.statLines) {
      const statEl = document.createElement('div');
      statEl.className = 'skill-tooltip-stat-line';
      if (typeof line === 'string') {
        statEl.textContent = line;
      } else {
        for (const segment of line) {
          const segmentEl = document.createElement('span');
          segmentEl.textContent = segment.text;
          if (segment.color) {
            segmentEl.style.color = segment.color;
          }
          statEl.appendChild(segmentEl);
        }
      }
      statsEl.appendChild(statEl);
    }
    tooltip.appendChild(statsEl);
  }
}

function renderTooltipNodeContent(tooltip: HTMLElement, content: HTMLElement): void {
  tooltip.innerHTML = '';
  tooltip.appendChild(content);
}

function positionTooltipAboveAnchor(tooltip: HTMLElement, anchorEl: HTMLElement): void {
  // Force layout after content changes so getBoundingClientRect sees the final tooltip size.
  tooltip.offsetWidth;
  const anchor = anchorEl.getBoundingClientRect();
  const tip = tooltip.getBoundingClientRect();
  const margin = 12;
  let left = anchor.left + anchor.width / 2 - tip.width / 2;
  let top = anchor.top - tip.height - 8;
  if (left < margin) left = margin;
  if (left + tip.width > window.innerWidth - margin) left = window.innerWidth - tip.width - margin;
  if (top < margin) top = anchor.bottom + 8;
  tooltip.style.left = `${Math.round(left)}px`;
  tooltip.style.top = `${Math.round(top)}px`;
}

function positionTooltipElement(tooltip: HTMLElement, clientX: number, clientY: number): void {
  const margin = 12;
  let left = clientX + 14;
  let top = clientY - 14;
  const rect = tooltip.getBoundingClientRect();

  if (left + rect.width > window.innerWidth - margin) {
    left = clientX - rect.width - 14;
  }
  if (top + rect.height > window.innerHeight - margin) {
    top = window.innerHeight - rect.height - margin;
  }
  if (top < margin) {
    top = margin;
  }
  if (left < margin) {
    left = margin;
  }

  tooltip.style.left = `${Math.round(left)}px`;
  tooltip.style.top = `${Math.round(top)}px`;
}

export class ActionTooltip {
  private tooltipEl: HTMLDivElement | null = null;

  public show(content: ActionTooltipContent, clientX: number, clientY: number): void {
    const tooltip = this.ensureTooltipElement();
    tooltip.classList.remove('skill-tooltip-node-host');
    renderActionTooltipContent(tooltip, content);
    tooltip.style.display = 'block';
    positionTooltipElement(tooltip, clientX, clientY);
  }

  public showAbove(content: ActionTooltipContent, anchorEl: HTMLElement): void {
    const tooltip = this.ensureTooltipElement();
    tooltip.classList.remove('skill-tooltip-node-host');
    renderActionTooltipContent(tooltip, content);
    tooltip.style.display = 'block';
    positionTooltipAboveAnchor(tooltip, anchorEl);
  }

  public showNodeAbove(content: HTMLElement, anchorEl: HTMLElement): void {
    const tooltip = this.ensureTooltipElement();
    tooltip.classList.add('skill-tooltip-node-host');
    renderTooltipNodeContent(tooltip, content);
    tooltip.style.display = 'block';
    positionTooltipAboveAnchor(tooltip, anchorEl);
  }

  public move(clientX: number, clientY: number): void {
    if (!this.tooltipEl || this.tooltipEl.style.display === 'none') return;
    positionTooltipElement(this.tooltipEl, clientX, clientY);
  }

  public hide(): void {
    if (!this.tooltipEl) return;
    this.tooltipEl.style.display = 'none';
  }

  private ensureTooltipElement(): HTMLDivElement {
    if (this.tooltipEl) return this.tooltipEl;

    const tooltip = document.createElement('div');
    tooltip.className = 'skill-tooltip';
    tooltip.style.display = 'none';
    document.body.appendChild(tooltip);
    this.tooltipEl = tooltip;
    return tooltip;
  }
}
